import { makeStyles } from "tss-react/mui"
import { Box, TextField } from "@mui/material"
import { faDollarSign, faFileLines } from "@fortawesome/pro-regular-svg-icons"

import { useAppLocalization } from "../../../../../../modules/localization/components/AppLocalizationProvider"
import { toDinero } from "../../../../../../modules/currency/currencyLib"
import { useMoney } from "../../../../../../modules/currency/useMoney"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { StandardAlertInfo } from "../../../../alert/StandardAlertInfo"
import { PaymentMethodType } from "../../../../../../modules/payments/paymentTypes"

import type { TakeawayPaymentMethod } from "../takeawayTypes"
import { isValidPaymentMethod, toEnumKey } from "./takeawayCheckoutLib"
import { TakeawayCheckoutSectionHeader } from "./TakeawayCheckoutSectionHeader"
import { TakeawayPaymentOption } from "./TakeawayPaymentOption"

function buildCardName(
    card: NonNullable<TakeawayPaymentMethod["card"]>,
    l10n: ReturnType<typeof useAppLocalization>["l10n"],
): string {
    const cardInfo =
        card.displayName ??
        `${card.type ?? ""} **** ${card.number ?? ""}${card.expiryDate ? ` (${card.expiryDate})` : ""}`.trim()
    return l10n.getString("takeaway-confirm-order-pay-using-card", { card: cardInfo })
}

function buildWalletName(
    method: TakeawayPaymentMethod,
    walletBalance: ReturnType<typeof toDinero>,
    moneyFactory: ReturnType<typeof useMoney>,
    l10n: ReturnType<typeof useAppLocalization>["l10n"],
): string {
    const baseName = l10n.getStringForEnum("purchases-payment-method", toEnumKey(method.value), method.value)
    return l10n.getString("takeaway-confirm-order-wallet-name-with-balance", {
        name: baseName,
        balance: moneyFactory.format(walletBalance),
    })
}

function buildNameOverride(
    method: TakeawayPaymentMethod,
    walletBalance: ReturnType<typeof toDinero> | undefined,
    moneyFactory: ReturnType<typeof useMoney>,
    l10n: ReturnType<typeof useAppLocalization>["l10n"],
): string | undefined {
    if (method.card) return buildCardName(method.card, l10n)
    if (method.value === PaymentMethodType.GOPAY_WALLET && walletBalance) {
        return buildWalletName(method, walletBalance, moneyFactory, l10n)
    }
    return undefined
}

const useStyles = makeStyles()((theme) => ({
    paymentList: {
        display: "flex",
        flexDirection: "column",
    },
    methodWrap: {
        borderBottom: `1px solid ${theme.palette.tailwind.slate[200]}`,
    },
    methodWrapSelected: {
        background: theme.palette.tailwind.blue[50],
    },
    noticeWrap: {
        padding: "0 1rem .875rem",
    },
    noticeAlert: {
        width: "100%",
        background: theme.palette.tailwind.sky[700],
        "& .MuiAlert-icon": {
            color: theme.palette.common.white,
        },
        "& .MuiAlert-message, & .MuiAlert-message *": {
            color: theme.palette.common.white,
            fontSize: "1rem",
        },
    },
}))

export type TakeawayCheckoutPaymentSectionProps = Readonly<{
    methods: ReadonlyArray<TakeawayPaymentMethod>
    selectedMethodValue: string
    walletTotal: ReturnType<typeof toDinero> | undefined
    walletBalance: ReturnType<typeof toDinero> | undefined
    moneyFactory: ReturnType<typeof useMoney>
    accountingValues: Record<string, string>
    notice?: string
    accountingDimensions?: TakeawayPaymentMethod["accounting"]
    onMethodSelect: (value: string) => void
    onAccountingChange: (name: string, value: string) => void
}>

export function TakeawayCheckoutPaymentSection({
    methods,
    selectedMethodValue,
    walletTotal,
    walletBalance,
    moneyFactory,
    accountingValues,
    notice,
    accountingDimensions,
    onMethodSelect,
    onAccountingChange,
}: TakeawayCheckoutPaymentSectionProps) {
    const { classes, cx } = useStyles()
    const { l10n } = useAppLocalization()

    return (
        <>
            <Box>
                <TakeawayCheckoutSectionHeader
                    icon={faDollarSign}
                    title={
                        <LocalizedStrict id="takeaway-confirm-order-payment-method-title">
                            <span>Payment Method</span>
                        </LocalizedStrict>
                    }
                />
                <Box className={classes.paymentList}>
                    {methods.map((method) => {
                        const showNotice =
                            selectedMethodValue === method.value &&
                            (notice !== undefined || method.value === PaymentMethodType.MOBILE_PAY)
                        const noticeText = notice ?? l10n.getString("takeaway-payment-mobilepay-notice")
                        return (
                            <Box
                                key={method.value}
                                className={cx(
                                    classes.methodWrap,
                                    selectedMethodValue === method.value && classes.methodWrapSelected,
                                )}
                            >
                                <TakeawayPaymentOption
                                    method={method}
                                    isSelected={selectedMethodValue === method.value}
                                    isDisabled={!isValidPaymentMethod(method, walletTotal, walletBalance)}
                                    nameOverride={buildNameOverride(method, walletBalance, moneyFactory, l10n)}
                                    onClick={() => onMethodSelect(method.value)}
                                />
                                {showNotice && (
                                    <Box className={classes.noticeWrap}>
                                        <StandardAlertInfo title={noticeText} className={classes.noticeAlert} />
                                    </Box>
                                )}
                            </Box>
                        )
                    })}
                </Box>
            </Box>

            {accountingDimensions?.dimensions && accountingDimensions.dimensions.length > 0 && (
                <Box>
                    <TakeawayCheckoutSectionHeader
                        icon={faFileLines}
                        title={
                            <LocalizedStrict id="takeaway-confirm-order-accounting-title">
                                <span>Accounting</span>
                            </LocalizedStrict>
                        }
                    />
                    {accountingDimensions.dimensions.map((dim) => (
                        <TextField
                            key={dim.id}
                            fullWidth
                            size="small"
                            label={dim.inputField.label ?? dim.inputField.name}
                            value={accountingValues[dim.inputField.name] ?? ""}
                            onChange={(e) => onAccountingChange(dim.inputField.name, e.target.value)}
                        />
                    ))}
                </Box>
            )}
        </>
    )
}
